import { formatterDateTime } from "~/js/utilities/formatter/formatterDateTime";

/**
 * Configuration constants for unit conversions.
 */
const CONVERSIONS = {
  METERS_TO_FEET: 3.28084,
  METERS_TO_MILES: 0.000621371192,
  KMH_TO_MPH: 0.621371192,
  KM_TO_MILES: 1.609344, // For pace calculation (1 mi = 1.609... km)
};

/**
 * Determines the unit system to use based on override or locale.
 * @param {boolean | undefined} isMetric - The user's explicit override (true=metric, false=imperial).
 * @param {string} locale - The BCP 47 language tag (e.g., 'en-US', 'de-DE').
 * @returns {'metric' | 'imperial' | 'mixed'} - The determined unit system.
 */
const getUnitSystem = (isMetric, locale) => {
  // 1. User has provided an explicit override
  if (typeof isMetric === "boolean") {
    return isMetric ? "metric" : "imperial";
  }

  // 2. No override, so guess based on locale region
  try {
    // Intl.Locale is modern and robust for parsing BCP 47 tags.
    const region = new Intl.Locale(locale)?.region;

    // The UK is a special "mixed" case
    if (region === "GB") {
      return "mixed";
    }

    // US, Liberia, and Myanmar are the primary imperial countries
    if (["US", "LR", "MM"].includes(region)) {
      return "imperial";
    }
  } catch (e) {
    // Fallback in case Intl.Locale fails (e.g., in very old environments)
    console.warn("Could not parse locale:", locale);
  }

  // 3. Default to metric for all other countries (e.g., 'de-DE', 'fr-FR', 'en-CA')
  return "metric";
};

/**
 * Formats a duration from total seconds to a human-readable string.
 * - If under 1 hour, format is "mm:ss".
 * - If 1 hour or more but less than 24 hours, format is "h:mm:ss".
 * - If 24 hours or more, format is "Xd Yh Zm".
 * @param {number} totalSeconds - The duration in seconds.
 * @returns {string} - The formatted string.
 */
const formatDuration = (totalSeconds) => {
  if (totalSeconds < 0) {
    return "0m";
  }

  const years = Math.floor(totalSeconds / (365 * 24 * 3600));
  let remainder = totalSeconds % (365 * 24 * 3600);

  const months = Math.floor(remainder / (30 * 24 * 3600));
  remainder %= 30 * 24 * 3600;

  const weeks = Math.floor(remainder / (7 * 24 * 3600));
  remainder %= 7 * 24 * 3600;

  const days = Math.floor(remainder / (24 * 3600));
  remainder %= 24 * 3600;

  const hours = Math.floor(remainder / 3600);
  remainder %= 3600;

  const minutes = Math.floor(remainder / 60);

  const parts = [];
  if (years > 0) parts.push(`${years}y`);
  if (months > 0) parts.push(`${months}mo`);
  if (weeks > 0) parts.push(`${weeks}w`);
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  return parts.length > 0 ? parts.join(" ") : "0m";
};

/**
 * Formats a pace from total seconds to mm:ss.
 * @param {number} seconds - The pace in seconds (per km or mi).
 * @returns {string} - The formatted string (e.g., "5:30").
 */
const formatPace = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/**
 * Formats a raw metric value from a GPX file into a display-ready object.
 *
 * @param {number} rawValue - The value in its base metric unit.
 * - distance: meters
 * - elevation: meters
 * - speed: km/h
 * - pace: decimal minutes per kilometer (e.g., 5.5 for 5:30)
 * - duration: seconds
 * - heartrate: bpm
 * - cadence-run: spm (steps per minute)
 * - cadence-cycle: rpm (revolutions per minute)
 * - temperature: Celsius
 * @param {'distance' | 'elevation' | 'speed' | 'pace' | 'duration' | 'heartrate' | 'cadence-run' | 'cadence-cycle' | 'temperature'} type - The type of data to format.
 * @param {boolean} [isMetric] - Optional. Force metric (true) or imperial (false). Defaults to locale.
 * @param {string} [locale] - Optional. BCP 47 language tag (e.g., 'de-DE'). Defaults to browser locale.
 * @returns {{value: string, unit: string}} - An object with the formatted value and unit.
 */
export const formatGpxValue = (
  rawValue,
  type,
  isMetric,
  locale,
  activityType,
) => {
  // Fallback to browser defaults if not provided
  const effectiveLocale = locale || navigator.language || "en-US";

  // Determine the system ('metric', 'imperial', 'mixed')
  const unitSystem = getUnitSystem(isMetric, effectiveLocale);

  // Helper to format numbers according to the user's locale (for commas vs. periods)
  const formatNumber = (num, options) => {
    return new Intl.NumberFormat(effectiveLocale, options).format(num);
  };
  switch (type) {
    case "distance": {
      // Metric (km) vs. Imperial/Mixed (mi)
      if (unitSystem === "metric") {
        return {
          value: formatNumber(rawValue / 1000, {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          }),
          unit: "km",
        };
      }
      return {
        value: formatNumber(rawValue * CONVERSIONS.METERS_TO_MILES, {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }),
        unit: "mi",
      };
    }
    case "gradient": {
      return {
        value: formatNumber(rawValue, { maximumFractionDigits: 2 }),
        unit: "Deg",
      };
    }
    case "date": {
      return {
        value: formatterDateTime(rawValue, effectiveLocale).date,
        unit: "",
      };
    }

    case "time": {
      return {
        value: formatterDateTime(rawValue, effectiveLocale).time,
        unit: "",
      };
    }
    case "datetime": {
      return {
        value: formatterDateTime(rawValue, effectiveLocale).timeDate,
        unit: "",
      };
    }
    case "timeDateLong": {
      return {
        value: formatterDateTime(rawValue, effectiveLocale).timeDateLong,
        unit: "",
      };
    }

    case "elevation": {
      // Metric/Mixed (m) vs. Imperial (ft)
      if (unitSystem === "imperial") {
        return {
          value: formatNumber(rawValue * CONVERSIONS.METERS_TO_FEET, {
            maximumFractionDigits: 0,
          }),
          unit: "ft",
        };
      }
      return {
        value: formatNumber(rawValue, { maximumFractionDigits: 0 }),
        unit: "m",
      };
    }

    case "speed": {
      // Metric (km/h) vs. Imperial/Mixed (mph)
      if (unitSystem === "metric") {
        return {
          value: formatNumber(rawValue, { maximumFractionDigits: 1 }),
          unit: "km/h",
        };
      }
      return {
        value: formatNumber(rawValue * CONVERSIONS.KMH_TO_MPH, {
          maximumFractionDigits: 1,
        }),
        unit: "mph",
      };
    }

    case "pace": {
      // Base value is assumed to be decimal minutes per kilometer (e.g., 5.5)

      // 1. Convert the decimal minutes (min/km) to total seconds (s/km)
      const secondsPerKm = rawValue * 60;

      if (unitSystem === "metric") {
        return {
          value: formatPace(secondsPerKm), // e.g., formatPace(5.5 * 60) -> "5:30"
          unit: "min/km",
        };
      }

      // 2. Convert seconds per km to seconds per mile
      const secondsPerMi = secondsPerKm * CONVERSIONS.KM_TO_MILES;

      return {
        value: formatPace(secondsPerMi), // e.g., formatPace(330 * 1.609...) -> "8:51"
        unit: "min/mi",
      };
    }

    case "temperature": {
      // Metric/Mixed (°C) vs. Imperial (°F)
      if (unitSystem === "imperial") {
        const fahrenheit = (rawValue * 9) / 5 + 32;
        return {
          value: formatNumber(fahrenheit, { maximumFractionDigits: 0 }),
          unit: "°F",
        };
      }
      return {
        value: formatNumber(rawValue, { maximumFractionDigits: 0 }),
        unit: "°C",
      };
    }

    case "slope": {
      return {
        value: formatNumber(rawValue, { maximumFractionDigits: 1 }),
        unit: "°",
      };
    }

    // --- System-Independent Units ---

    case "duration": {
      // Formats seconds into hh:mm:ss
      return {
        value: formatDuration(rawValue),
        unit: "", // The value is self-explanatory
      };
    }

    case "heartrate": {
      return {
        value: formatNumber(rawValue, { maximumFractionDigits: 0 }),
        unit: "bpm",
      };
    }

    case "heartRatePercent": {
      return {
        value: rawValue,
        unit: "%",
      };
    }

    case "calories": {
      return {
        value: rawValue,
        unit: "kCal",
      };
    }

    case "steps": {
      return {
        value: rawValue,
        unit: "",
      };
    }

    case "cadence": {
      let unit = "spm";
      if (activityType) {
        const lowerType = activityType.toLowerCase();
        if (
          lowerType.includes("cycle") ||
          lowerType.includes("bike") ||
          lowerType.includes("biking")
        ) {
          unit = "rpm";
        }
      }
      return {
        value: formatNumber(rawValue, { maximumFractionDigits: 0 }),
        unit: unit,
      };
    }

    case "cadence-run": {
      return {
        value: formatNumber(rawValue, { maximumFractionDigits: 0 }),
        unit: "spm",
      };
    }

    case "cadence-cycle": {
      return {
        value: formatNumber(rawValue, { maximumFractionDigits: 0 }),
        unit: "rpm",
      };
    }

    default:
      return {
        value: formatNumber(rawValue, {}),
        unit: "",
      };
  }
};
